import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const footerLinks = {
    productos: [
      { name: 'GPAX Builder', href: '/gpax-builder', external: false },
      { name: 'GPAX Transformer', href: '/gpax-transformer', external: false },
      { name: 'GPAX Audit', href: 'https://gpax.io/', external: true }
    ],
    empresa: [
      { name: t('footer.whyInfoware'), href: '/why-infoware' },
      { name: t('footer.ourCommitment'), href: '/#commitment' },
      { name: t('footer.certifications'), href: '/why-infoware#certifications' }
    ],
    legal: [
      { name: t('footer.terms'), href: 'https://support.gpax.io/portal/es/kb/articles/terminos-de-servicio', external: true },
      { name: t('footer.privacy'), href: 'https://support.gpax.io/portal/es/kb/articles/acuerdo-de-confidencialidad', external: true },
      { name: t('footer.sla'), href: 'https://support.gpax.io/portal/es/kb/articles/acuerdo-de-nivel-de-servicio-sla', external: true }
    ]
  };

  const socialLinks = [
    { icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/company/infoware-s-a-', label: 'LinkedIn' },
    { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/gpax.io', label: 'Facebook' },
    { icon: 'ri-instagram-fill', href: 'https://www.instagram.com/infoware.cr/', label: 'Instagram' },
    { icon: 'ri-mail-fill', href: 'mailto:soluciones@infowarecr.com', label: 'Email' }
  ];

  return (
    <footer className="bg-slate-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <img 
              src="https://storage.helloreaddy.io/project_files/c3b631e3-3a43-495e-bee9-2c780af1817b/02f9c3b8-9cdb-4cea-8e59-c64769907f2b_compressed_Logo_Infoware.webp" 
              alt="Infoware S. A." 
              className="h-10 w-auto"
            />
            <p className="text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-orange-500 text-gray-300 hover:text-white transition-all duration-300 transform hover:scale-110 cursor-pointer"
                  aria-label={social.label}
                >
                  <i className={`${social.icon} text-lg`}></i>
                </a>
              ))}
            </div>
            
            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/50624479912"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              {t('footer.talk')}
            </a>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t('footer.products')}</h3>
            <ul className="space-y-3">
              {footerLinks.productos.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:text-orange-500 transition-colors duration-300 cursor-pointer"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm hover:text-orange-500 transition-colors duration-300 cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks.empresa.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-orange-500 transition-colors duration-300 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Legal */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t('footer.contact')}</h3>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-sm">
                <i className="ri-phone-line text-orange-500 mr-2 mt-1"></i>
                <a href="tel:+50624479912" className="hover:text-orange-500 transition-colors cursor-pointer">
                  +506 2447 9912
                </a>
              </li>
              <li className="flex items-start text-sm">
                <i className="ri-mail-line text-orange-500 mr-2 mt-1"></i>
                <a href="mailto:soluciones@infowarecr.com" className="hover:text-orange-500 transition-colors cursor-pointer break-all">
                  soluciones@infowarecr.com
                </a>
              </li>
            </ul>
            
            <h4 className="text-white font-semibold text-sm mb-3">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="text-xs hover:text-orange-500 transition-colors duration-300 cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} Infoware S. A. {t('footer.rights')}
            </p>
            
            {/* Certifications */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                  <i className="ri-shield-check-line text-base text-cyan-400"></i>
                </span>
                <span className="text-xs text-gray-400">ISO 27001</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                  <i className="ri-leaf-line text-base text-green-400"></i>
                </span>
                <span className="text-xs text-gray-400">{t('footer.carbonNeutral')}</span>
              </div>
            </div>
            
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
